import Link from '@/routing/Link'
import { DatasetDocumentCover } from '@/components/library/DatasetDocumentCover'
import { useTranslation } from 'react-i18next'
import { KnowledgeDocumentListItem, KnowledgeSearchHit } from '@digital-twins/shared-types'
import { Card, CardContent } from '@/components/ui/card'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Badge } from '@/components/ui/badge'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { Skeleton } from '@/components/ui/skeleton'
import {
  coverBackgroundForToken,
  documentCoverUrl,
  documentTypeLabel,
  displayKnowledgeTypeLabel,
  documentTitle,
} from './libraryHelpers'
import { highlightQueryInSnippet } from './highlightQueryInSnippet'
import { KnowledgeSearchRelevantPages } from './KnowledgeSearchRelevantPages'

interface KnowledgeSearchResultsProps {
  query: string
  hits: Array<KnowledgeSearchHit>
  searching: boolean
  documents?: Array<KnowledgeDocumentListItem>
}

function searchHitDescriptionParts(
  hit: KnowledgeSearchHit,
  doc: KnowledgeDocumentListItem | undefined,
  query: string,
) {
  const candidate = hit.summary?.trim() || doc?.excerpt?.trim() || ''
  const summary = candidate.startsWith('[No extractable text') ? '' : candidate
  if (summary) {
    const parts = highlightQueryInSnippet(summary, query)
    if (parts.some((part) => part.match)) {
      return parts
    }
  }
  const firstSnippet = hit.pages?.[0]?.snippet?.trim()
  if (firstSnippet) {
    return highlightQueryInSnippet(firstSnippet, query)
  }
  if (summary) {
    return highlightQueryInSnippet(summary, query)
  }
  return null
}

export function KnowledgeSearchResults({
  query,
  hits,
  searching,
  documents = [],
}: KnowledgeSearchResultsProps) {
  const { t } = useTranslation('app')
  if (searching) {
    return (
      <div className="space-y-3" data-testid="knowledge-search-loading">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 rounded-2xl" />
        ))}
      </div>
    )
  }

  if (hits.length === 0) {
    return (
      <LobbyEmptyState
        title={t('library.noMatches')}
        description={t('library.searchEmptyBody', { query })}
      >
        <div data-testid="knowledge-search-empty" />
      </LobbyEmptyState>
    )
  }

  const docsByToken = new Map(documents.map((d) => [d.token, d]))

  return (
    <div className="min-w-0 space-y-3" data-testid="knowledge-search-results">
      {hits.map((hit, index) => {
        const doc = docsByToken.get(hit.token)
        const typeLabel = doc ? displayKnowledgeTypeLabel(documentTypeLabel(doc)) : null
        const coverSrc = doc
          ? documentCoverUrl(doc)
          : coverBackgroundForToken(hit.token)
        const descriptionParts = searchHitDescriptionParts(hit, doc, query)

        return (
          <Card
            key={hit.token}
            className="rounded-2xl border-border bg-card-inset shadow-none dark:bg-card"
            data-testid={`knowledge-search-hit-${hit.token}`}
          >
            <CardContent className="flex flex-col p-0">
              <div className="flex gap-4 p-4 pb-3">
                <Link
                  href={`/knowledge/${hit.token}`}
                  className="relative h-[132px] w-24 shrink-0 overflow-hidden rounded-xl border border-border bg-muted"
                  data-testid={`knowledge-search-hit-cover-link-${hit.token}`}
                >
                  {coverSrc ? <img
                    src={coverSrc}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                    data-testid={`knowledge-search-hit-cover-${hit.token}`}
                  /> : <DatasetDocumentCover />}
                </Link>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {doc?.isSimulationArtifact ? (
                      <Badge variant="secondary" data-testid="knowledge-search-sim-badge">
                        {t('library.type.simulationReport')}
                      </Badge>
                    ) : typeLabel ? (
                      <Badge variant="tag">{typeLabel}</Badge>
                    ) : null}
                    {doc?.isScannedPdf ? (
                      <Badge variant="warning" data-testid="knowledge-search-scanned-badge">
                        {t('library.noSearchableText')}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Link
                      href={`/knowledge/${hit.token}`}
                      className="text-primary hover:underline"
                      data-testid={`knowledge-search-hit-link-${hit.token}`}
                    >
                      <Heading level={3} className="line-clamp-2 font-serif text-lg font-normal text-primary">
                        {doc ? documentTitle(doc) : hit.originalFilename || t('library.untitled')}
                      </Heading>
                    </Link>
                    {descriptionParts ? (
                      <Text
                        size="sm"
                        className="line-clamp-4 leading-relaxed text-foreground/85"
                        data-testid={`knowledge-search-hit-description-${hit.token}`}
                      >
                        {descriptionParts.map((part, partIndex) =>
                          part.match ? (
                            <strong
                              key={`${part.text}-${partIndex}`}
                              className="font-semibold text-foreground"
                            >
                              {part.text}
                            </strong>
                          ) : (
                            <span key={`${part.text}-${partIndex}`}>{part.text}</span>
                          ),
                        )}
                      </Text>
                    ) : null}
                  </div>
                </div>
              </div>
              <KnowledgeSearchRelevantPages
                documentToken={hit.token}
                query={query}
                pages={hit.pages ?? []}
                pageHitCount={hit.pageHitCount}
                defaultExpanded={index === 0}
                coverImageUrl={coverSrc}
              />
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
